/**
 * A `StringSearchWidgets` renders all the `StringSearchField` components according to the
 * `field_schema` objects returned in the `fields` field from a `/api/v1/search` endpoint. Notice
 * that all `fields_schema` objects which are string search fields are aggregated into a list and
 * rendered together in this widget
 */

import React from 'react';
import immutable from 'immutable';

import StringSearchField from './StringSearchField';
import ConstValues from 'utils/const';
import SearchFieldsActions from 'actions/search/SearchFieldsActions';

const optionKeyToLabelMapping = immutable.Map({
  none: 'None of',
  all_of: 'All of',
  some_of: 'Some of',
  not_contain: "Doesn't Contain",
  contains: 'Contains',
});
// TODO: Update the `GET /api/v1/search` endpoint to also return a list of m.SearchField.output_name
// values whose search widgets should not be rendered in the search sidebar
const stringSearchFieldsToSkip = immutable.List(['CRM Link']);

class StringSearchWidgets extends React.Component {
  /**
   * Update a string `searchField`'s match with `value`, which means we should call
   * `SearchFieldsActions.updateStringSearchField` to update the searchExpression
   * as well as `SearchFieldsActions.updateSearchFieldsState` to update the searchFieldsState
   * in `SearchFieldsStore`
   * @param  {records/Field} searchField A string searchField
   * @param  {string} value       A string value which should be added to the searchExpression
   *                              for this `searchField`
   */
  onUpdateStringSearchFieldWithNewValue(searchField, value) {
    SearchFieldsActions.updateStringSearchField(searchField, value);
  }

  /**
   * Return the key of the selected option for a `searchField`
   * @param  {record/Field} searchField A search field
   * @return {string}             Key of the selected option if there is one or the default key if there
   *                                  is none
   */
  getStringSearchFieldSelectedOption(searchField) {
    const state = this.props.searchFieldsState.get(searchField.name);
    if (state) {
      return state.get('selectedOptionKey');
    } else {
      if (searchField.type === 'finite_string') {
        return ConstValues.finiteStringSearchFieldDefaultSelectedOptionKey;
      } else if (searchField.type === 'infinite_string') {
        return ConstValues.infiniteStringSearchFieldDefaultSelectedOptionKey;
      }
    }
  }

  /**
   * Return a label that's renderable on the UI for search field `sf`
   * @param  {records/Field} sf A search field object
   * @return {string}    A rederable label for `sf`
   */
  getStringSearchFieldSelectedOptionLabel(sf) {
    return optionKeyToLabelMapping.get(this.getStringSearchFieldSelectedOption(sf));
  }

  /**
   * Return a list of string values from `SearchFieldsStore.getState().get('searchFieldsState')` for
   * `this.props.searchField`
   * @param {records/Field} sf A searchField object
   * @return {immutable.List} A list of string values ready for rendering
   */
  getSelectedStringValues(sf) {
    const state = this.props.searchFieldsState.get(sf.name);
    if (state) {
      return immutable.List(state.get('values'));
    } else {
      return null;
    }
  }

  /**
   * Update `selectedOptionKey` in `SearchFieldsStore.getState().get('searchFieldsState')`
   * as well as `SearchFieldsStore.getState().get('searchExpression')` using corresponding
   * actions defined in `SearchFieldsActions`
   * @param  {records/Field} sf  A search field object
   * @param  {string} key An `IconButton` option key string
   */
  onUpdateSelectedOptionKey(sf, key) {
    SearchFieldsActions.updateStringSearchFieldSelectedOptionKey(sf, key);
  }

  /**
   * Return the `toggled` state value for a `searchField`
   * @param  {immutable/Record} searchField A search field
   * @return {Boolean}            true if the searchField is toggled and false if not
   */
  getStringSearchFieldsToggled(searchField) {
    if (this.props.searchFieldsToggled) {
      return this.props.searchFieldsToggled.get(searchField.name);
    }
    return false;
  }

  /**
   * Render a string search field for the provided search field.
   * @param  {searchField} The search field to render.
   * @return {JSX} A string search widget.
   */
  renderComponent(sf) {
    return (
      <StringSearchField
        key={sf.name}
        searchField={sf}
        toggled={this.getStringSearchFieldsToggled(sf)}
        selectedStringValues={this.getSelectedStringValues(sf)}
        selectedOptionKey={this.getStringSearchFieldSelectedOption(sf)}
        selectedOptionLabel={this.getStringSearchFieldSelectedOptionLabel(sf)}
        updateLastToggledOnSearchWidget={this.props.updateLastToggledOnSearchWidget}
        onUpdateSelectedOptionKey={this.onUpdateSelectedOptionKey.bind(this)}
        shouldFocus={
          this.props.shouldRenderToggled &&
          this.getStringSearchFieldsToggled(sf) &&
          sf.name === this.props.lastToggledOnSearchWidget
        }
        shouldSortByToggled={this.props.shouldSortByToggled}
        onUpdateStringSearchFieldWithNewValue={this.onUpdateStringSearchFieldWithNewValue.bind(
          this
        )}
      />
    );
  }

  /**
   * Returns a boolean that indicates whether the Search Field should not be rendered.
   * @return {boolean} When true indicates that the Search Field should not be rendered.
   */
  shouldNotRenderComponent(sf) {
    if (
      (this.props.shouldRenderToggled && this.getStringSearchFieldsToggled(sf)) ||
      (this.props.shouldRenderNotToggled && !this.getStringSearchFieldsToggled(sf))
    ) {
      let searchFieldsToSkip = stringSearchFieldsToSkip;
      if (this.props.isSearchingLibraryTags) {
        searchFieldsToSkip = searchFieldsToSkip.push('Library');
      }
      if (this.props.isSearchingSelf) {
        searchFieldsToSkip = searchFieldsToSkip.push('Caller');
      }
      return searchFieldsToSkip.includes(sf.name);
    } else {
      return true;
    }
  }

  render() {
    const searchFields = this.props.searchFields.sort(sf => sf.order);
    return (
      <div className="search-widget">
        {searchFields
          .filterNot(this.shouldNotRenderComponent, this)
          .map(this.renderComponent, this)
          .toArray()}
      </div>
    );
  }
}

export default StringSearchWidgets;
